import React, { useState } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  useMutation,
  useQuery,
  useApolloClient,
  createHttpLink,
  from,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { LoginForm } from "./components/layout/LoginForm";
import { Layout } from "./components/layout/Layout";
import { Dashboard } from "./components/layout/Dashboard";
import { EmployeeManagement } from "./components/employee/EmployeeManagement";
import { EventsList } from "./components/event/EventsList";
import { Reports } from "./components/report/Reports";
import { Calendar } from "./components/leave/Calendar";
import { LeaveManagement } from "./components/leave/LeaveManagement";
import { LeaveTypeManagement } from "./components/leave/LeaveTypeManagement";
import { ContractTypeManagement } from "./components/contract-type/ContractTypeManagement";
import { DisabilityDegreeManagement } from "./components/disability-degree/DisabilityDegreeManagement";
import { PositionManagement } from "./components/position/PositionManagement";
import { DepartmentManagement } from "./components/department/DepartmentManagement";
import { ShiftManagement } from "./components/shift/ShiftManagement";
import DeviceManagement from "./components/device/DeviceManagement";
import { SystemSettingsManagement } from "./components/settings/SystemSettingsManagement";
import { EmailNotificationsPage } from "./components/settings/email/EmailNotificationsPage";
import { WhatsNewDialog } from "./components/changelog/WhatsNewDialog";
import { ChangelogPage } from "./components/changelog/ChangelogPage";
import { RolesPage } from "./pages/RolesPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { MentionsPage } from "./pages/MentionsPage";
import { StaffingPlanPage } from "./components/planning/StaffingPlanPage";
import { StaffingRequirementsPage } from "./components/planning/StaffingRequirementsPage";
import { RequirePermission } from "./components/RequirePermission";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthContext, AuthEmployee, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";
import {
  ADMIN_LOGIN_MUTATION,
  ADMIN_LOGOUT_MUTATION,
  GET_CURRENT_USER,
  GET_UNREAD_HIGH_IMPORTANCE_CHANGELOGS,
  MARK_ALL_CHANGELOGS_AS_READ,
} from "./graphql/queries";

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || "http://localhost:3000/graphql",
  credentials: "include", // This ensures cookies are sent with requests
});

// WebSocket link for subscriptions
const wsUrl = process.env.REACT_APP_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/graphql`;

const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUrl,
    retryAttempts: Infinity, // Keep retrying
    shouldRetry: () => true,
    keepAlive: 15000, // Send keepalive every 15s to prevent idle timeout
    lazy: true, // Don't connect until subscription is used
    connectionAckWaitTimeout: 30000, // Wait 30s for connection acknowledgement
    retryWait: async (retries) => {
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const delay = Math.min(1000 * Math.pow(2, retries), 30000);
      await new Promise(resolve => setTimeout(resolve, delay));
    },
    connectionParams: async () => {
      // Include credentials for authentication
      return {
        credentials: 'include',
      };
    },
    on: {
      connected: () => {
        console.debug('✅ WebSocket connected');
      },
      error: (error) => {
        // Silently handle connection errors - they will auto-retry
        console.debug('⚠️ WebSocket error (will retry):', error);
      },
      closed: (event) => {
        console.debug('🔌 WebSocket closed (will retry):', event);
      },
    },
  })
);

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
    },
  };
});

// Create errorLink with logout functionality
// Only triggers logout on actual session/token errors (expired JWT, missing token).
// Does NOT logout on permission errors (e.g. MANAGER accessing ADMIN-only endpoint).
// Skips GetCurrentUser query — that's handled by the retry logic in AdminAppContent.
const createErrorLink = (logoutFn: () => void) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    // Don't trigger logout during initial auth check — handled by retry logic
    if (operation.operationName === "GetCurrentUser") return;

    if (graphQLErrors) {
      for (const { message, extensions } of graphQLErrors) {
        const isSessionError =
          message === "Unauthorized" ||
          extensions?.code === "UNAUTHENTICATED";

        if (isSessionError) {
          logoutFn();
          return;
        }
      }
    }

    if (networkError) {
      // Only 401 = session error. 403 = permission error, don't logout.
      if ("statusCode" in networkError && networkError.statusCode === 401) {
        logoutFn();
      }
    }
  });

// Events page wrapper that reads filters from search params
function EventsPageWrapper() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterEmployeeId = searchParams.get("employeeId") || undefined;
  const filterStartDate = searchParams.get("startDate") || undefined;
  const filterEndDate = searchParams.get("endDate") || undefined;

  const handleClearFilters = () => {
    setSearchParams({});
  };

  return (
    <RequirePermission permission="employees.view">
      <EventsList
        filterEmployeeId={filterEmployeeId}
        filterStartDate={filterStartDate}
        filterEndDate={filterEndDate}
        onClearFilters={handleClearFilters}
      />
    </RequirePermission>
  );
}

// Reports page wrapper that uses navigate for cross-page navigation
function ReportsPageWrapper({ userRole }: { userRole?: string }) {
  const navigate = useNavigate();

  const handleNavigateToEvents = (employeeId: string, startDate: string, endDate: string, e?: React.MouseEvent) => {
    const path = `/events?employeeId=${employeeId}&startDate=${startDate}&endDate=${endDate}`;
    if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  return (
    <RequirePermission permission="reports.view">
      <Reports onNavigateToEvents={handleNavigateToEvents} currentUserRole={userRole} />
    </RequirePermission>
  );
}

// Dashboard wrapper
function DashboardPageWrapper({ userRole, currentUserId }: { userRole?: string; currentUserId?: string }) {
  const navigate = useNavigate();
  return <Dashboard currentUserRole={userRole} currentUserId={currentUserId} onNavigateToReports={(e?: React.MouseEvent) => {
    if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
      window.open('/reports', '_blank');
    } else {
      navigate("/reports");
    }
  }} />;
}

function AdminAppContent() {
  // Get auth state from context
  const {
    isAuthenticated,
    currentUser,
    setIsAuthenticated,
    setCurrentUser,
    authChecked,
    setAuthChecked,
  } = useAuth();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState("");

  const [login, { loading: loginLoading }] = useMutation(ADMIN_LOGIN_MUTATION);
  const [logout] = useMutation(ADMIN_LOGOUT_MUTATION);
  const [showWhatsNew, setShowWhatsNew] = useState(false);

  // Fetch unread high importance changelogs
  const { data: changelogsData } = useQuery(GET_UNREAD_HIGH_IMPORTANCE_CHANGELOGS, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const [markAllAsRead] = useMutation(MARK_ALL_CHANGELOGS_AS_READ);
  const client = useApolloClient();

  // Auth check with retry logic — retries up to 3 times on transient failures.
  // On definitive auth errors (expired token), clears cookie and shows login immediately.
  React.useEffect(() => {
    if (authChecked || isAuthenticated) return;

    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1500; // 1.5s between retries
    let cancelled = false;

    async function checkAuth(attempt: number) {
      try {
        const { data } = await client.query({
          query: GET_CURRENT_USER,
          fetchPolicy: "network-only",
        });

        if (cancelled) return;

        if (data?.me) {
          setCurrentUser(data.me);
          setIsAuthenticated(true);
        }
        setAuthChecked(true);
      } catch (error: any) {
        if (cancelled) return;

        // Check if this is a definitive auth error (expired/invalid token)
        const isAuthError = error?.graphQLErrors?.some(
          (e: any) =>
            e.message === "Unauthorized" ||
            e.extensions?.code === "UNAUTHENTICATED"
        );

        if (isAuthError) {
          // Token expired/invalid — clear stale cookie and show login
          try {
            await client.mutate({ mutation: ADMIN_LOGOUT_MUTATION });
          } catch {}
          if (!cancelled) setAuthChecked(true);
        } else if (attempt < MAX_RETRIES) {
          // Transient error (network blip, cold start) — retry after delay
          await new Promise((r) => setTimeout(r, RETRY_DELAY));
          if (!cancelled) checkAuth(attempt + 1);
        } else {
          // All retries exhausted — show login
          if (!cancelled) setAuthChecked(true);
        }
      }
    }

    checkAuth(1);

    // Timeout fallback — 15s covers Railway cold starts
    const timeout = setTimeout(() => {
      if (!cancelled) setAuthChecked(true);
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [authChecked, isAuthenticated, client, setCurrentUser, setIsAuthenticated, setAuthChecked]);

  // Show WhatsNew dialog if there are unread changelogs
  React.useEffect(() => {
    if (isAuthenticated && changelogsData?.unreadHighImportanceChangelogs?.length > 0) {
      setShowWhatsNew(true);
    }
  }, [isAuthenticated, changelogsData]);

  const handleLogin = async (loginOrEmail: string, password: string) => {
    try {
      setLoginError("");

      const { data } = await login({
        variables: { data: { loginOrEmail, password } },
      });

      if (data?.adminLogin?.employee) {
        const employee = data.adminLogin.employee;
        setCurrentUser(employee);

        // Small delay to ensure cookies are set before enabling queries
        setTimeout(() => {
          setIsAuthenticated(true);
        }, 100);
      }
    } catch (error: any) {
      setLoginError(error.message || "Nieprawidłowy email lub hasło");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsAuthenticated(false);
      setCurrentUser(null);
      navigate("/");
    }
  };

  const userRole = currentUser?.role;

  // Show loading while checking authentication (only if not checked yet)
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Sprawdzanie autoryzacji...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !currentUser) {
    return (
      <LoginForm
        onLogin={handleLogin}
        isLoading={loginLoading}
        error={loginError}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route element={<Layout employee={currentUser} onLogout={handleLogout} />}>
          <Route index element={<DashboardPageWrapper userRole={userRole} currentUserId={currentUser?.id} />} />
          <Route path="employees" element={<RequirePermission permission="employees.view"><EmployeeManagement currentUserRole={userRole} /></RequirePermission>} />
          <Route path="positions" element={<RequirePermission permission="positions.view"><PositionManagement currentUserRole={userRole} /></RequirePermission>} />
          <Route path="departments" element={<RequirePermission permission="departments.view"><DepartmentManagement currentUserRole={userRole} /></RequirePermission>} />
          <Route path="shifts" element={<RequirePermission permission="shifts.view"><ShiftManagement currentUserRole={userRole} /></RequirePermission>} />
          <Route path="events" element={<EventsPageWrapper />} />
          <Route path="reports" element={<ReportsPageWrapper userRole={userRole} />} />
          <Route path="calendar" element={<RequirePermission permission="leaves.view"><Calendar currentUserRole={userRole} /></RequirePermission>} />
          <Route path="leaves" element={<RequirePermission permission="leaves.view"><LeaveManagement currentUserRole={userRole || ''} currentUserId={currentUser?.id} /></RequirePermission>} />
          <Route path="leave-types" element={<RequirePermission permission="leaveTypes.manage"><LeaveTypeManagement /></RequirePermission>} />
          <Route path="contract-types" element={<RequirePermission permission="contractTypes.manage"><ContractTypeManagement /></RequirePermission>} />
          <Route path="disability-degrees" element={<RequirePermission permission="disabilityDegrees.manage"><DisabilityDegreeManagement /></RequirePermission>} />
          <Route path="devices" element={<RequirePermission permission="devices.view"><DeviceManagement currentUserRole={userRole!} /></RequirePermission>} />
          <Route path="system-settings" element={<RequirePermission permission="settings.manage"><SystemSettingsManagement /></RequirePermission>} />
          <Route path="email-notifications" element={<RequirePermission permission="settings.manage"><EmailNotificationsPage /></RequirePermission>} />
          <Route path="roles" element={<RequirePermission permission="roles.manage"><RolesPage /></RequirePermission>} />
          <Route path="projects" element={
            <RequirePermission anyOf={['projects.view_assigned','projects.view_all']}>
              <ProjectsPage currentUserId={currentUser?.id} />
            </RequirePermission>
          } />
          <Route path="projects/:id" element={
            <RequirePermission anyOf={['projects.view_assigned','projects.view_all']}>
              <ProjectDetailPage currentUserId={currentUser?.id} />
            </RequirePermission>
          } />
          <Route path="projects/:id/:tab" element={
            <RequirePermission anyOf={['projects.view_assigned','projects.view_all']}>
              <ProjectDetailPage currentUserId={currentUser?.id} />
            </RequirePermission>
          } />
          <Route path="planning" element={<RequirePermission permission="planning.view"><StaffingPlanPage /></RequirePermission>} />
          <Route path="planning/requirements" element={<RequirePermission permission="planning.view"><StaffingRequirementsPage /></RequirePermission>} />
          <Route path="changelog" element={<ChangelogPage />} />
          <Route path="mentions" element={<MentionsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      <WhatsNewDialog
        open={showWhatsNew}
        onClose={() => setShowWhatsNew(false)}
        changelogs={changelogsData?.unreadHighImportanceChangelogs || []}
        onMarkAsRead={() => {}}
        onMarkAllAsRead={async () => {
          await markAllAsRead();
        }}
      />
    </>
  );
}

function AdminApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthEmployee | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Use ref to keep logout function up-to-date without recreating Apollo Client
  const logoutRef = React.useRef(() => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setAuthChecked(true);
    // Clear stale cookie server-side (fire and forget — adminLogout is unguarded)
    clientRef.current?.mutate({ mutation: ADMIN_LOGOUT_MUTATION }).catch(() => {});
  });

  // Stable Apollo Client - created once, never recreated on re-render
  const clientRef = React.useRef<ApolloClient<any> | null>(null);
  if (!clientRef.current) {
    const errorLink = createErrorLink(() => logoutRef.current());

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      from([errorLink, authLink, httpLink])
    );

    clientRef.current = new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache({
        typePolicies: {
          // Planning mutations return the whole board; keying it by week lets
          // Apollo update the open board automatically. Nested lists are
          // always replaced wholesale by the server.
          StaffingPlanBoard: {
            keyFields: ["weekStart"],
            fields: {
              dates: { merge: false },
              workDays: { merge: false },
              employees: { merge: false },
              slots: { merge: false },
              assignments: { merge: false },
              absences: { merge: false },
              holidays: { merge: false },
              headcount: { merge: false },
              violations: { merge: false },
              constraints: { merge: false },
            },
          },
          StaffingRequirementsMatrix: {
            keyFields: [],
            fields: {
              positions: { merge: false },
              slots: { merge: false },
              requirements: { merge: false },
            },
          },
        },
      }),
    });
  }

  const permissions = currentUser?.permissions ?? [];

  const authContextValue = {
    isAuthenticated,
    currentUser,
    setIsAuthenticated,
    setCurrentUser,
    authChecked,
    setAuthChecked,
    permissions,
    hasPermission: (perm: string) => permissions.includes(perm),
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      <ApolloProvider client={clientRef.current}>
        <AdminAppContent />
      </ApolloProvider>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Toaster position="bottom-right" richColors closeButton />
      <AdminApp />
    </ThemeProvider>
  );
}

export default App;
